import { Component, OnInit, ViewChild, AfterViewInit, OnDestroy, Input } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { WizardService, NavigateArgs } from 'src/app/wizard-bar/services/wizard.service';
import { of, Observable, forkJoin, Subscription } from 'rxjs';
import { MatTableDataSource } from '@angular/material/table';
import { QuoteRequestService } from '@app/shared/services/quote-request.service';
import { CustomService } from 'src/app/custom/services/custom.service';
import { MatSort } from '@angular/material/sort';
import { MatDialog } from '@angular/material/dialog';
import { ValidationMessageCode } from 'src/app/custom/models/validation-message-codes';
import { ConfirmComponent } from 'src/app/shared/components/confirm/confirm.component';
import { RenewalsFixDataComponent } from '../../components/renewals-fix-data/renewals-fix-data-component';
import { DateConstants } from '@app/constants/date-constants';
import { Store } from '@ngrx/store';
import { UtilityHubIdentity } from '@auth/models/utility-hub-identity';
import { QuoteRequestState } from '@app/shared';
import { CommodityType } from '@app/shared/enums/commodity-type';
import { CustomConstants } from '@app/custom/models/custom-constants';
import { SalesType } from '@app/shared/enums/sales-type';
import { AppState } from '@app/state/app.state';
import { salesTypeSelector, utilityUOMSelector } from '@utility-information/state/selectors/utility-information.selectors';
import { loggedInUserSelector, loggedInUtilityHubIdentitySelector } from '@app/state/app.selectors';
import { RenewalsFileData } from '../../models/renewals-file-data';
import { RenewalsValidationResult } from '../../models/renewals-validation-result';
import { RenewalsUtilityAccountValidationResults } from '../../models/renewals-utility-account-validation-result';
import { SetOriginalQuoteRequestId } from '@app/renewals/state/actions/renewals-module.actions';
import { originalQuoteRequestIdSelector } from '@app/renewals/state/selectors/renewals-module.selectors';

@Component({
  selector: 'app-renewals-custom-file-validation',
  templateUrl: './renewals-custom-file-validation.component.html',
  styleUrls: ['./renewals-custom-file-validation.component.scss'],
})
export class RenewalsCustomFileValidationComponent implements OnInit, AfterViewInit, OnDestroy {
  public Commodity = CommodityType;
  public quoteRequestId: any;
  public dateFormat = DateConstants.LOCALE_FORMAT_SHORT();

  @ViewChild(MatSort) sort: MatSort;

  @Input() pricingMethod: string;

  displayedColumns = [
    'utilityAccountNumber',
    'startDate',
    'endDate',
    'meterReadings',
    'uploadConsumption',
    'days',
    'blanks',
    'fileName',
    'info',
    'fix',
    'remove',
  ];
  minDataDays = 60;
  quoteRequestState: QuoteRequestState;
  utilityAccounts: RenewalsUtilityAccountValidationResults[];
  fileData: RenewalsFileData[] = [];
  dataSource = new MatTableDataSource<RenewalsFileData>(this.fileData);
  zerosCriteria: number;
  gapsCriteria: number;
  userIdentity: UtilityHubIdentity;
  routeSubscription$: Subscription;
  selectLoggedInUserSubscription$: Subscription;
  selectLoggedInUtilityHubIdentitySubscription$: Subscription;
  loadSubscription$: Subscription;
  dialogAfterClosedSubscription$: Subscription;
  deleteUtilityAccountAfterClosedSubscription$: Subscription;
  salesType$: Observable<string>;
  utilityUOM$: Observable<string>;
  originalQuoteRequestIdSelector$: Subscription;
  originalQuoteRequestId: number | null;

  constructor(
    private route: ActivatedRoute,
    private wizardService: WizardService,
    private quoteRequestService: QuoteRequestService,
    private customQuoteService: CustomService,
    protected dialog: MatDialog,
    private store: Store<AppState>
  ) {
    this.salesType$ = store.select(salesTypeSelector);
    this.utilityUOM$ = this.store.select(utilityUOMSelector);
  }

  ngOnDestroy(): void {
    this.routeSubscription$?.unsubscribe();
    this.selectLoggedInUserSubscription$?.unsubscribe();
    this.selectLoggedInUtilityHubIdentitySubscription$.unsubscribe();
    this.loadSubscription$?.unsubscribe();
    this.deleteUtilityAccountAfterClosedSubscription$?.unsubscribe();
    this.dialogAfterClosedSubscription$?.unsubscribe();
    this.originalQuoteRequestIdSelector$?.unsubscribe();
  }

  ngOnInit(): void {
    this.wizardService.bindStep(this.route, (args) => this.save(args));
    this.routeSubscription$ = this.route.params.subscribe((x) => {
      this.quoteRequestId = null;
      if (!isNaN(x.quoteRequestId)) {
        this.quoteRequestId = Number(x.quoteRequestId);
        this.load();
      }
    });

    this.selectLoggedInUserSubscription$ = this.store.select(loggedInUserSelector).subscribe((user) => {
      if (user && !user.expired) {
        this.selectLoggedInUtilityHubIdentitySubscription$ = this.store.select(loggedInUtilityHubIdentitySelector).subscribe((uhUser) => {
          this.userIdentity = uhUser;
        });
      }
    });

    this.originalQuoteRequestIdSelector$ = this.store.select(originalQuoteRequestIdSelector).subscribe((originalQuoteRequestId) => {
      this.originalQuoteRequestId = originalQuoteRequestId;
    });
  }

  ngAfterViewInit() {
    this.dataSource.sort = this.sort;
  }

  mapUtilityAccountsToFileValidationTable() {
    this.fileData = [];
    let validatorResults: RenewalsValidationResult[] = [];

    this.utilityAccounts.forEach((element) => {
      const totalDays = element.totalDays;
      let blankDays = 0;
      let zeroDays = 0;
      const forecasted = element.isForecasted;

      element.consumptionDataValidationResults.forEach((val) => {
        if (val.name === 'MinimumDataValidator') {
          // We need one for between min data and 365 days.
          validatorResults.push({
            ...val,
            valid: totalDays >= 365 || (totalDays >= val.criteria && forecasted),
            name: 'ForecastRequiredValidator',
          });
        }
        if (val.name === 'ZeroDataValidator') {
          zeroDays += val.actual;
        }
        if (val.name === 'GapsValidator') {
          blankDays += val.actual;
        }

        validatorResults.push(val);
      });

      this.salesType$
        .subscribe((salesType) => {
          const displayData: RenewalsFileData = {
            utilityAccountNumber: element.utilityAccountNumber,
            startDate: element.startDate ?? '',
            endDate: element.endDate ?? '',
            meterReadings: element.meterReadings ?? '',
            uploadConsumption: element.uploadConsumption,
            days: totalDays,
            blanks: blankDays,
            zeroes: zeroDays,
            file: element.file,
            valid: salesType === SalesType.renewal ? true : validatorResults.every((x) => x.valid),
            validatorResults,
            validationMessage: '',
          };
          validatorResults = [];

          displayData.validationMessage = this.getValidationMessage(displayData);

          this.fileData.push(displayData);
        })
        .unsubscribe();
    });
  }

  getValidationMessage(displayData: RenewalsFileData): string {
    let messageCodes: ValidationMessageCode[] = [];
    if (!displayData.file) {
      messageCodes.push(ValidationMessageCode.NO_DATA);
      return messageCodes.join('\n');
    }

    displayData.validatorResults.forEach((val) => {

      if (!val.valid) {

        switch (val.name) {
          case 'FutureDateValidator':
            messageCodes.push(ValidationMessageCode.FUTURE_DATE);
            break;

          case 'ForecastRequiredValidator':
          case 'MinimumDataValidator':
            if (!messageCodes.includes(ValidationMessageCode.LESS_THAN_60_DAYS)) {
              messageCodes.push(ValidationMessageCode.LESS_THAN_60_DAYS);
            }
            break;

          case 'GapsValidator':
            messageCodes.push(ValidationMessageCode.TOO_MANY_GAPS);
            break;

          case 'MaximumDataAgeValidator':
            messageCodes.push(ValidationMessageCode.DATA_TOO_OLD);
            break;

          case 'OverlappingDateValidator':
            messageCodes.push(ValidationMessageCode.TOO_MANY_OVERLAPPING_DATES);
            break;

          case 'GapsBetweenDatesValidator':
            messageCodes.push(ValidationMessageCode.GAPS_EXIST);
            break;

          case 'ZeroDataValidator':
            messageCodes.push(ValidationMessageCode.TOO_MANY_ZEROS);
            break;

          default:
            break;
        }
      }
    });

    const msg = messageCodes.join('\n');

    return msg;
  }

  load() {
    this.loadSubscription$ = forkJoin({
      state: this.quoteRequestService.getQuoteRequestState(this.quoteRequestId),
      utilityAccounts: this.customQuoteService.getFileValidation(this.quoteRequestId),
    }).subscribe((res) => {
      this.quoteRequestState = res.state;
      this.utilityAccounts = res.utilityAccounts;
      this.store.dispatch(new SetOriginalQuoteRequestId(this.quoteRequestState.renewedFromQuoteId));

      this.mapUtilityAccountsToFileValidationTable();

      this.dataSource = new MatTableDataSource(this.fileData);
      this.dataSource.sort = this.sort;
      console.log(JSON.stringify(res.utilityAccounts));
    });
  }

  deleteUtilityAccount(utilityAccountNumber: string) {
    const dialogRef = this.dialog.open(ConfirmComponent, {
      width: '25%',
      data: {
        title: 'Delete Utility Account Number',
        message: 'Are you sure you want to delete this Utility Account Number and all data associated with it?',
        primaryButtonText: 'Delete',
      },
    });

    this.deleteUtilityAccountAfterClosedSubscription$ = dialogRef.afterClosed().subscribe((confirmed) => {
      if (confirmed) {
        const warningDialogRef = this.wizardService.resolveExpireQuoteWarningDialog();
        if (warningDialogRef.required) {
          this.dialogAfterClosedSubscription$ = warningDialogRef.dialogRef.afterClosed().subscribe((cfrmWarning) => {
            if (cfrmWarning) {
              this.customQuoteService.deleteUtilityAccountNumber(this.quoteRequestId, utilityAccountNumber).subscribe(() => {
                this.load();
              });
            }
          });
        }
      }
    });
  }

  public showFixDialog(data: RenewalsFileData) {
    const parserNameValue = this.quoteRequestState.commodity == CommodityType.Gas ? 'GasFileParser' : 'StandardScalarFileParser';
    const dialogRef = this.dialog.open(RenewalsFixDataComponent, {
      width: '40%',
      data: {
        pricingMethod: this.pricingMethod,
        quoteRequestId: this.quoteRequestId,
        utilityAccountNumber: data.utilityAccountNumber,
        validationMessage: data.validationMessage,
        parseRequestData: {
          parserName: parserNameValue,
          quoteRequestId: this.quoteRequestId,
          utilityAccountNumber: data.utilityAccountNumber,
        },
      },
    });

    dialogRef.afterClosed().subscribe(() => {
      this.load();
    });
  }

  public save(args: NavigateArgs): Observable<any> {
    const data = this.dataSource.data;
    if (data.every((x) => x.valid)) {
      return of(true);
    } else {
      if ((args.direction && args.direction < 0) || this.wizardService.clickedStepIsPreviousStep(CustomConstants.wizardSteps, args.step)) {
        return of(true);
      } else {
        return of(false);
      }
    }
  }

  public next() {
    this.wizardService.next();
  }

  public back() {
    this.wizardService.previous();
  }

  public isReadOnly() {
    return this.quoteRequestService.isReadonly(this.quoteRequestId, this.quoteRequestState?.status, this.userIdentity?.id, this.quoteRequestState?.ownerUserId);
  }
}
